import { get } from 'widgets/toolbox/util';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { TInputCheckboxInstance } from 'widgets/forms/InputCheckbox';
import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { TInputRadioInstance } from 'widgets/forms/InputRadio';

type TOrderObject = {
    [key: string]: number | string | boolean | Array<number | string | boolean>;
};
type TCustomerObject = {
    isAuthenticated: boolean;
    [key: string]: number | string | boolean | Array<number | string | boolean>;
};

/**
 * @param BillingForm Base widget for extending
 * @returns Billing Form
 */
function CoreBillingFormClassCreator(BillingForm: ReturnType<typeof import('widgets/checkout/BillingForm').default>) {
    class CoreBillingForm extends BillingForm {
        loadMoreClicked = false;

        isPickupShipping = false;

        isUseShippingPreUnchecked = false;

        prefs() {
            return {
                isBopis: false,
                makeDefaultFieldId: '',
                saveAddressFieldId: '',
                enabledCountryRestriction: false,
                ...super.prefs(),
                errorMessageLabelRef: 'billingErrorMessage',
                billingErrorPleaseFillRef: 'billingErrorPleaseFill'
            };
        }

        init() {
            super.init();

            this.initMakeDefaultField();
        }

        /**
         * @description Initialize make default field
         */
        initMakeDefaultField() {
            this.getById(this.prefs().saveAddressFieldId, (saveAddressField: TInputRadioInstance) => {
                this.getById(this.prefs().makeDefaultFieldId, (makeDefaultField: TInputRadioInstance) => {
                    makeDefaultField.toggle(saveAddressField.ref('field').prop('checked'));
                });
            });
        }

        /**
         * @override
         * @description Return applicable addresses for current form
         * @param order - order data
         * @param customer - customer data
         * @returns returns billing addresses
         */
        getAddressOptions(order, customer): Array<Record<string, string>> {
            const isPickupPointShipment = !!get(order, 'shipping.0.isPickupPointShipment');
            const shippingAddress = get(order, 'shipping.0.shippingAddress');
            const isBopis = get(order, 'shipping.0.isBopis', false);
            const billingAddress = get(order, 'billing.billingAddress.address');
            const customerAddresses = this.prefs().enabledCountryRestriction
                ? customer.filteredAddressesByAllowedBillingCountries
                : customer.addresses;

            const allAddresses = [...customerAddresses];

            if (!isPickupPointShipment && !isBopis && shippingAddress) {
                allAddresses.push(shippingAddress);
            }

            if (billingAddress) {
                allAddresses.push(billingAddress);
            }

            return allAddresses;
        }

        /**
         * @description Handles submit Form
         * @returns Promise object represents server response for shipping methods updating
         */
        handleSubmit() {
            if (!this.isChildrenValid() || this.submitting) {
                return Promise.resolve(null);
            }

            this.busy();

            this.submitting = true;

            this.ref(this.prefs().errorMessageLabel).hide();
            this.ref(this.prefs().billingErrorPleaseFillRef).hide();

            return submitFormJson(this.getFormUrl(), this.getFormFields(), this.ref('self').attr('method') === 'GET' ? 'GET' : 'POST')
                .then(response => {
                    this.onSubmitted(response);

                    return response;
                })
                .catch(this.onError.bind(this))
                .finally(this.afterSubmission.bind(this));
        }

        /**
         * @description Form validate
         * @returns boolean value is Form input valid
         */
        validate(): boolean {
            this.ref(this.prefs().billingErrorPleaseFillRef).show();

            return super.validate();
        }

        /**
         * @description Submit form handler
         */
        async submitAsync() {
            let isValid = false;

            if (this.isValid()) {
                const result = await this.handleSubmit();

                if (!result?.success && this.isBillingFormHasToBeValidated()) {
                    return Promise.reject(new Error('Server response is not successful'));
                }

                isValid = result?.success;
            }

            this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                const useShipping = Boolean(useAddressTypeCheckbox.getValue());

                if (useShipping) {
                    useAddressTypeCheckbox.uncheck();
                    this.isUseShippingPreUnchecked = true;
                }
            });
            this.showAddressForm();
            this.validate();

            return isValid ? Promise.resolve() : Promise.reject(new Error('Validation failed'));
        }

        /**
         * @description Process form submission response
         * @param data - Server response object
         * @param data.success - is it success
         * @param data.serverErrors - server errors
         * @param data.fieldErrors - field errors
         * @param data.cartError - is it cart error
         * @param data.redirectUrl - redirect url
         */
        onSubmitted(data: TServerResponse) {
            super.onSubmitted(data);

            if (!data.success && data.fieldErrors && this.isBillingFormHasToBeValidated()) {
                const errorMessageLabel = this.ref(this.prefs().billingErrorPleaseFillRef).show();
                const element = errorMessageLabel.get();

                if (element) {
                    scrollWindowTo(element, true);
                }

                this.eventBus().emit('checkout.billing.step.error', this);
            } else {
                this.ref(this.prefs().billingErrorPleaseFillRef).hide();
            }

            if (data.success) {
                this.getById(this.prefs().saveAddressCheckbox, (checkbox: TInputCheckboxInstance) => {
                    if (checkbox.getValue() === 'true') {
                        this.handleGTMUpdates();
                    }
                });
            }
        }

        updateCustomer(customer: TCustomerObject) {
            this.customer = customer;
        }

        updateState(order: TOrderObject, customer: TCustomerObject) {
            this.updateOrder(order);
            this.updateCustomer(customer);

            this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                const useShipping = Boolean(useAddressTypeCheckbox.getValue());

                if (useShipping) {
                    this.activateAddressSelector();
                }
            });
        }

        /**
         * @description Toggle use shipping or billing functionality after address selector activation
         * @param useAddressTypeCheckbox - Use shipping or billing checkbox component
         * @param isBillingFormShouldBeCleared - Flag to determine if billing form should be cleared when checkbox is unchecked
         *
         * @override this function overrides the parent class function in order not to show address selector for unauthenticated users
         * in default implementation it shows address selector for unauthenticated users based on the checkbox value and based address data.
         */
        toggleUseAddressType(useAddressTypeCheckbox: TInputCheckboxInstance, isBillingFormShouldBeCleared = true) {
            this.ref(this.prefs().billingErrorPleaseFillRef).hide();

            const isChecked = Boolean(useAddressTypeCheckbox.getValue());
            const customer = this.order?.customer as TCustomerObject | undefined;
            const isAuthenticated = customer?.isAuthenticated;

            if (!isChecked && (!isAuthenticated || (isAuthenticated && !this.getAddressOptions({}, this.customer).length))) {
                if (isBillingFormShouldBeCleared && !this.isPickupShipping) {
                    this.clearAddressFields(false);
                    this.eventBus().emit('dimensional.dropdown.reinit');
                }

                this.showAddressForm();
            } else {
                super.toggleUseAddressType(useAddressTypeCheckbox, isBillingFormShouldBeCleared);
            }

            this.ref('noInvoiceMessage')?.toggle(!isChecked);
        }

        /**
         * @description Handle click on add new address button.
         * Toggle saved and new address blocks
         * @listens dom#click
         */
        addNewAddress(): void {
            super.addNewAddress();
            this.toggleSaveAddressCheckbox(false);
        }

        /**
         * @description Toggle make default field visibility based on save address widget state
         * @param saveAddressField - Save address widget instance
         */
        toggleMakeDefaultEv(saveAddressField: TInputRadioInstance): void {
            this.getById(this.prefs().makeDefaultFieldId, (makeDefaultField: TInputRadioInstance) => {
                makeDefaultField.toggle(saveAddressField.ref('field').prop('checked'));
                makeDefaultField.ref('field').prop('checked', false);
            });
        }

        /**
         * Activates the address selector.
         * If the customer is not authenticated, triggers `onAddressSelectorActivated` and resolves immediately.
         * Otherwise, calls the parent class's `activateAddressSelector`.
         *
         * @override not necessary to show address selector for unauthenticated users.
         *
         * @returns A promise that resolves after activation, or the parent method's return value.
         */
        activateAddressSelector(): Promise<void | null> | undefined {
            const customer = this.order?.customer as TCustomerObject | undefined;
            const isAuthenticated = customer?.isAuthenticated;
            const isGiftCertExclusive = this.order?.isGiftCertExclusive;

            if (this.isUseShippingPreUnchecked) {
                this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                    useAddressTypeCheckbox.check();
                    this.isUseShippingPreUnchecked = false;
                });
            }

            this.isPickupShipping = !!get(this.order, 'shipping.0.isPickupPointShipment');

            if (this.isPickupShipping) {
                this.toggleDeliveryType(this.isPickupShipping);
            } else if (isGiftCertExclusive || (this.prefs().isBopis && this.getAddressOptions(this.order, this.customer).length === 0)) {
                this.showAddressForm();
            } else if (!this.prefs().isBopis && !isAuthenticated) {
                return Promise.resolve().then(() => {
                    this.onAddressSelectorActivated();
                });
            }

            return super.activateAddressSelector();
        }

        /**
         * @description As we don't have payment method selected anymore
         * we don't validate only in case useShipping
         */
        isBillingFormHasToBeValidated(): boolean {
            if (this.isPickupShipping) {
                return true;
            }

            const useShipping = this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                return useAddressTypeCheckbox.getValue();
            });

            return !useShipping;
        }

        /**
         * @description Toggles the delivery type between pickup and shipping.
         * If the delivery type is pickup, it shows the address form and toggles the 'use address type' checkbox visibility.
         *
         * @param isPickupShipping - boolean indicating if the delivery type is pickup
         */
        toggleDeliveryType(isPickupShipping: boolean) {
            this.isPickupShipping = isPickupShipping;

            const customer = this.order?.customer as TCustomerObject | undefined;
            const isAuthenticated = customer?.isAuthenticated;

            if (isPickupShipping && !isAuthenticated) {
                this.showAddressForm();
                this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                    useAddressTypeCheckbox.uncheck();
                    useAddressTypeCheckbox.hide();
                });
            } else {
                this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                    const preparedAddressOptions = this.getPreparedAddressOptions(this.order, this.customer);

                    if (isPickupShipping && preparedAddressOptions.customerAddresses.length === 0) {
                        this.showAddressForm();
                        useAddressTypeCheckbox.uncheck();
                        useAddressTypeCheckbox.hide();

                        return;
                    }

                    useAddressTypeCheckbox.show();

                    this.toggleUseAddressType(useAddressTypeCheckbox, false);
                });
            }
        }

        addressLoadMore() {
            this.loadMoreClicked = true;
        }

        /**
         * @description Handles GTM tracking updates
         * @emits account.resetPassword
         */
        handleGTMUpdates() {
            this.eventBus().emit('account.update', { type: 'address_book' });
        }

        /**
         * @description Returns selected address
         * @param filteredAddresses Filtered addresses
         * @returns Sorted addresses
         */
        getSelectedAddress(filteredAddresses: Array<Record<string, string>>): string {
            const selectedBillingAddress = filteredAddresses.find(address => address.isDefaultBillingAddress);

            return selectedBillingAddress?.addressUUID || (filteredAddresses && filteredAddresses[0]?.addressUUID);
        }
    }

    return CoreBillingForm;
}

export type TCoreBillingForm = ReturnType<typeof CoreBillingFormClassCreator>;

export type TCoreBillingFormInstance = InstanceType<TCoreBillingForm>;

export default CoreBillingFormClassCreator;
